import { useState, useEffect } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { navigateToLastTVView } from "../../utils/tvNavigation";
import {
  LayoutDashboard,
  Calendar,
  Settings,
  LogOut,
  Menu,
  Sun,
  Moon,
  Clock,
  Tv,
  CheckCircle,
  UtensilsCrossed,
  BookOpen,
  ChevronDown,
  User,
  CreditCard,
  Loader2,
} from "lucide-react";
import { Mosque } from "@phosphor-icons/react";
import { useAuth } from "../../contexts/AuthContext";
import { useTheme } from "../../theme/ThemeContext";
import { auth } from "../../config/firebase";
import { Button } from "../ui/button";
import { Avatar, AvatarFallback } from "../ui/avatar";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  DropdownMenuSeparator,
  DropdownMenuLabel,
} from "../ui/dropdown-menu";
import { Sheet, SheetContent, SheetTrigger } from "../ui/sheet";
import { Separator } from "../ui/separator";
import { cn } from "@/lib/utils";
import { BRAND, EDITORIAL_FONT } from "../editorial/EditorialPrimitives";

const MASJID_API_URL = import.meta.env.VITE_MASJID_API;
const MASJID_BILLING_PORTAL_CACHE_KEY = "masjid_billing_portal_url";
const MASJID_BILLING_PORTAL_TTL_MS = 10 * 60 * 1000; // 10 minutes

const getMenuStructure = (userProfile, canManageMasjidSubscription = true) => {
  // Halal restaurant user menu
  if (userProfile?.role === "halal" && !userProfile?.isAdmin) {
    return {
      main: [
        {
          text: "Home",
          icon: LayoutDashboard,
          path: "/halal",
        },
        {
          text: "Settings",
          icon: Settings,
          path: "/settings",
        },
      ],
    };
  }

  // Regular user menu
  if (!userProfile?.isAdmin) {
    return {
      main: [
        {
          text: "Home",
          icon: LayoutDashboard,
          path: "/",
        },
        {
          text: "Settings",
          icon: Settings,
          path: "/settings",
        },
      ],
    };
  }

  // Admin menu
  const isSuperAdmin = userProfile?.role === "super";
  const adminDashboardPath = isSuperAdmin ? "/admin" : "/masjid-dashboard";

  const adminMenu = {
    main: [
      {
        text: "Dashboard",
        icon: LayoutDashboard,
        path: adminDashboardPath,
      },
    ],
  };

  const manageItems = [
    {
      text: "Prayer Times",
      icon: Clock,
      path: "/prayer-times",
    },
    {
      text: "Events",
      icon: Calendar,
      path: "/events",
    },
    {
      text: "TV Viewer",
      icon: Tv,
      action: "tvViewer",
    },
  ];

  if (isSuperAdmin) {
    // Super admin keeps a Manage dropdown with the existing items
    adminMenu.manage = {
      label: "Manage",
      icon: (props) => <Mosque {...props} />,
      items: manageItems,
    };
  } else {
    // Masjid admin: only show Dashboard, Subscription, and Settings in nav.
    // Add a dedicated Subscription item that goes straight to the billing portal,
    // but only if this admin is allowed to manage the masjid subscription.
    if (canManageMasjidSubscription) {
      adminMenu.main.push({
        text: "Subscription",
        icon: CreditCard,
        action: "masjidBillingPortal",
      });
    }
  }

  // Super admin gets approval items
  if (isSuperAdmin) {
    adminMenu.approvals = {
      label: "Approvals",
      icon: CheckCircle,
      items: [
        {
          text: "Masjid Approvals",
          icon: CheckCircle,
          path: "/admin/approvals",
        },
        {
          text: "Halal Approvals",
          icon: UtensilsCrossed,
          path: "/admin/halal-approvals",
        },
      ],
    };
  }

  // Settings at the end:
  // - Super admins keep Settings in the top nav
  // - Masjid admins access settings from within the dashboard instead of the nav bar
  if (isSuperAdmin) {
    adminMenu.main.push({
      text: "Settings",
      icon: Settings,
      path: "/settings",
    });
  }

  return adminMenu;
};

function MobileMenu({
  menuStructure,
  currentPath,
  onNavigate,
  onClose,
  onMasjidBillingPortal,
  onTVViewer,
  billingLoading,
}) {
  return (
    <div className="flex h-full flex-col py-6">
      <nav className="flex-1 space-y-2 px-4">
        {/* Main items (excluding Settings to place after Manage) */}
        {(menuStructure.main || [])
          .filter((item) => item.path !== "/settings")
          .map((item) => {
            const Icon = item.icon;
            const hasPath = Boolean(item.path);
            const isActive = hasPath && currentPath === item.path;
            const isPortalAction = item.action === "masjidBillingPortal";
            const isTVViewerAction = item.action === "tvViewer";
            const isLoading = isPortalAction && billingLoading;

            const handleClick = () => {
              if (isPortalAction) {
                onMasjidBillingPortal?.();
              } else if (isTVViewerAction) {
                onTVViewer?.();
              } else if (item.path) {
                onNavigate(item.path);
              }
              onClose();
            };

            return (
              <button
                key={item.text}
                onClick={handleClick}
                disabled={isLoading}
                className={cn(
                  "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                  isActive
                    ? "bg-primary text-primary-foreground"
                    : "hover:bg-accent hover:text-accent-foreground"
                )}
              >
                {isLoading ? (
                  <Loader2 className="h-5 w-5 animate-spin" />
                ) : (
                  <Icon className="h-5 w-5" />
                )}
                <span>{item.text}</span>
              </button>
            );
          })}

        {/* Grouped items */}
        {menuStructure.manage && (
          <>
            <Separator className="my-4" />
            <div className="px-2 py-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                {menuStructure.manage.label}
              </p>
            </div>
            {menuStructure.manage.items.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;

              return (
                <button
                  key={item.text}
                  onClick={() => {
                    if (item.action === "masjidBillingPortal") {
                      onMasjidBillingPortal?.();
                    } else if (item.action === "tvViewer") {
                      onTVViewer?.();
                    } else if (item.path) {
                      onNavigate(item.path);
                    }
                    onClose();
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.text}</span>
                </button>
              );
            })}
          </>
        )}

        {/* Settings placed after Manage */}
        {(() => {
          const settingsItem = (menuStructure.main || []).find(
            (i) => i.path === "/settings"
          );
          if (!settingsItem) return null;
          const Icon = settingsItem.icon;
          const isActive = currentPath === settingsItem.path;
          return (
            <button
              key={settingsItem.text}
              onClick={() => {
                onNavigate(settingsItem.path);
                onClose();
              }}
              className={cn(
                "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                isActive
                  ? "bg-primary text-primary-foreground"
                  : "hover:bg-accent hover:text-accent-foreground"
              )}
            >
              <Icon className="h-5 w-5" />
              <span>{settingsItem.text}</span>
            </button>
          );
        })()}

        {/* Approvals */}
        {menuStructure.approvals && (
          <>
            <Separator className="my-4" />
            <div className="px-2 py-2">
              <p className="text-xs font-semibold uppercase text-muted-foreground">
                {menuStructure.approvals.label}
              </p>
            </div>
            {menuStructure.approvals.items.map((item) => {
              const Icon = item.icon;
              const isActive = currentPath === item.path;

              return (
                <button
                  key={item.text}
                  onClick={() => {
                    onNavigate(item.path);
                    onClose();
                  }}
                  className={cn(
                    "flex w-full items-center gap-3 rounded-xl px-4 py-3 text-sm font-medium transition-all duration-200",
                    isActive
                      ? "bg-primary text-primary-foreground"
                      : "hover:bg-accent hover:text-accent-foreground"
                  )}
                >
                  <Icon className="h-5 w-5" />
                  <span>{item.text}</span>
                </button>
              );
            })}
          </>
        )}
      </nav>
    </div>
  );
}

export default function AppLayout({ children }) {
  const [mobileOpen, setMobileOpen] = useState(false);
  const [billingLoading, setBillingLoading] = useState(false);
  const [billingPortalUrl, setBillingPortalUrl] = useState(() => {
    if (typeof window === "undefined") return null;
    try {
      const raw = window.sessionStorage.getItem(
        MASJID_BILLING_PORTAL_CACHE_KEY
      );
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed.url || !parsed.ts) return null;
      if (Date.now() - parsed.ts > MASJID_BILLING_PORTAL_TTL_MS) {
        window.sessionStorage.removeItem(MASJID_BILLING_PORTAL_CACHE_KEY);
        return null;
      }
      return parsed.url;
    } catch {
      return null;
    }
  });
  const navigate = useNavigate();
  const location = useLocation();
  const { user, userProfile, logout } = useAuth();
  const { appearance, toggleTheme } = useTheme();
  const [canManageMasjidSubscription, setCanManageMasjidSubscription] = useState(true);

  useEffect(() => {
    const checkSubscriptionOwner = async () => {
      try {
        // Only check for logged-in masjid admins (non-super)
        if (!user || !userProfile?.isAdmin || userProfile?.role === "super") {
          setCanManageMasjidSubscription(false);
          return;
        }

        if (!MASJID_API_URL) {
          // If the masjid API isn't configured, default to hiding subscription management
          setCanManageMasjidSubscription(false);
          return;
        }

        const masjidId = userProfile.activeMasjidId || userProfile.masjidId;
        if (!masjidId) {
          setCanManageMasjidSubscription(false);
          return;
        }

        if (!auth.currentUser) {
          setCanManageMasjidSubscription(false);
          return;
        }

        const idToken = await auth.currentUser.getIdToken(true);
        const resp = await fetch(
          `${MASJID_API_URL}/v1/masjid/${encodeURIComponent(
            masjidId
          )}/subscription-owner`,
          {
            headers: {
              Authorization: `Bearer ${idToken}`,
            },
          }
        );

        if (!resp.ok) {
          setCanManageMasjidSubscription(false);
          return;
        }

        const data = await resp.json().catch(() => ({}));
        setCanManageMasjidSubscription(Boolean(data.isSubscriptionOwner));
      } catch (err) {
        console.error("Failed to check masjid subscription owner:", err);
        setCanManageMasjidSubscription(false);
      }
    };

    checkSubscriptionOwner();
  }, [user, userProfile]);

  const menuStructure = getMenuStructure(userProfile, canManageMasjidSubscription);

  const handleLogout = async () => {
    try {
      await logout();
      navigate("/login");
    } catch (error) {
      console.error("Logout error:", error);
    }
  };

  const handleMasjidBillingPortal = async () => {
    // Only masjid admins (non-super) should trigger the billing portal from nav
    if (!user || !userProfile?.isAdmin || userProfile?.role === "super") return;

    try {
      setBillingLoading(true);
      // If we already have a cached portal URL, use it immediately for Pro masjids.
      if (billingPortalUrl) {
        window.location.href = billingPortalUrl;
        return;
      }

      if (!auth.currentUser) {
        setBillingLoading(false);
        return;
      }

      const idToken = await auth.currentUser.getIdToken(true);

      // Helper: fall back to checkout session (for basic plan with no Stripe customer)
      const openCheckout = async () => {
        try {
          const checkoutResp = await fetch(
            `${MASJID_API_URL}/v1/billing/create-checkout-session`,
            {
              method: "POST",
              headers: {
                Authorization: `Bearer ${idToken}`,
                "Content-Type": "application/json",
              },
            }
          );

          if (!checkoutResp.ok) {
            console.error("Failed to create masjid checkout session");
            alert(
              "Unable to start subscription checkout. Please try again or contact support."
            );
            return;
          }

          const checkoutData = await checkoutResp.json();
          if (checkoutData.ok && checkoutData.url) {
            window.location.href = checkoutData.url;
          } else {
            console.error(
              "Unexpected response from masjid checkout endpoint:",
              checkoutData
            );
            alert("Unable to start subscription checkout. Please try again.");
          }
        } catch (err) {
          console.error("Error creating masjid checkout session:", err);
          alert("Unable to start subscription checkout. Please try again.");
        }
      };

      // First try to open the Stripe billing portal (for Pro masjids)
      const response = await fetch(
        `${MASJID_API_URL}/v1/billing/portal`,
        {
          method: "POST",
          headers: {
            Authorization: `Bearer ${idToken}`,
            "Content-Type": "application/json",
          },
        }
      );

      if (!response.ok) {
        // If there's no existing subscription, fall back to checkout for upgrade
        if (response.status === 404 || response.status === 400) {
          await openCheckout();
          return;
        }

        console.error("Failed to open masjid billing portal from nav");
        alert(
          "Unable to open billing portal. Please try again or contact support."
        );
        return;
      }

      const data = await response.json();
      if (data.ok && data.url) {
        setBillingPortalUrl(data.url);
        try {
          window.sessionStorage.setItem(
            MASJID_BILLING_PORTAL_CACHE_KEY,
            JSON.stringify({ url: data.url, ts: Date.now() })
          );
        } catch {
          // Ignore storage errors
        }
        window.location.href = data.url;
      } else {
        console.error(
          "Unexpected response from masjid billing portal endpoint:",
          data
        );
        alert("Unable to open billing portal. Please try again later.");
      }
    } catch (error) {
      console.error("Error opening masjid billing portal from nav:", error);
      alert("Unable to open billing portal. Please try again.");
    } finally {
      setBillingLoading(false);
    }
  };

  const isDark = appearance === "dark";
  const palette = isDark
    ? {
        bg: BRAND.ink,
        bgScrim: "rgba(26,23,19,0.85)",
        text: BRAND.ivory,
        muted: "#9a9288",
        border: "#2a2520",
        chip: "#ffffff10",
      }
    : {
        bg: BRAND.ivory,
        bgScrim: "rgba(250,247,241,0.88)",
        text: BRAND.ink,
        muted: BRAND.muted,
        border: BRAND.hairline,
        chip: "#00000008",
      };

  const Wordmark = ({ size = 24 }) => (
    <span
      style={{
        fontSize: size,
        fontWeight: 500,
        lineHeight: 1,
        letterSpacing: "-0.015em",
        fontFamily: EDITORIAL_FONT.sans,
        color: palette.text,
      }}
    >
      Muslim<span style={{ color: BRAND.gold }}>+</span>
    </span>
  );

  return (
    <div
      className="min-h-screen"
      style={{ background: palette.bg, color: palette.text, fontFamily: EDITORIAL_FONT.sans }}
    >
      {/* Top Navigation Bar */}
      <nav
        className="sticky top-0 z-50 backdrop-blur-xl"
        style={{
          background: palette.bgScrim,
          borderBottom: `1px solid ${palette.border}`,
          fontFamily: EDITORIAL_FONT.sans,
        }}
      >
        <div className="container mx-auto flex h-16 items-center justify-between gap-4 px-4 sm:h-[72px] sm:px-6 lg:px-10">
          {/* Brand */}
          <button
            type="button"
            onClick={() => navigate("/")}
            className="flex flex-shrink-0 items-center transition-opacity hover:opacity-80"
            aria-label="Muslim+ home"
          >
            <Wordmark size={24} />
          </button>

          {/* Desktop Navigation */}
          <div className="hidden flex-1 items-center justify-center gap-1 lg:flex">
            {(menuStructure.main || [])
              .filter((item) => item.path !== "/settings")
              .map((item) => {
                const hasPath = Boolean(item.path);
                const active = hasPath && location.pathname === item.path;
                const isPortalAction = item.action === "masjidBillingPortal";
                const isTVViewerAction = item.action === "tvViewer";
                const isLoading = isPortalAction && billingLoading;

                const handleClick = () => {
                  if (isPortalAction) {
                    handleMasjidBillingPortal();
                  } else if (isTVViewerAction) {
                    navigateToLastTVView(navigate);
                  } else if (item.path) {
                    navigate(item.path);
                  }
                };

                return (
                  <button
                    key={item.text}
                    onClick={handleClick}
                    disabled={isLoading}
                    className="relative inline-flex items-center gap-2 rounded-full px-4 py-2 text-sm transition-colors disabled:opacity-60"
                    style={{
                      color: active ? BRAND.gold : palette.text,
                      fontWeight: active ? 600 : 450,
                      background: active ? palette.chip : "transparent",
                    }}
                    onMouseEnter={(e) => {
                      if (!active) e.currentTarget.style.color = BRAND.gold;
                    }}
                    onMouseLeave={(e) => {
                      if (!active) e.currentTarget.style.color = palette.text;
                    }}
                  >
                    {isLoading ? (
                      <Loader2 className="h-4 w-4 animate-spin" />
                    ) : null}
                    <span>{item.text}</span>
                  </button>
                );
              })}

            {/* Manage Dropdown */}
            {menuStructure.manage && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    disabled={billingLoading}
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors disabled:opacity-60"
                    style={{ color: palette.text, fontWeight: 450 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.gold)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = palette.text)}
                  >
                    <span>{menuStructure.manage.label}</span>
                    <ChevronDown className="h-3.5 w-3.5" style={{ color: palette.muted }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-56 rounded-xl p-1.5"
                  style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
                >
                  {menuStructure.manage.items.map((item) => {
                    const Icon = item.icon;
                    const active = item.path ? location.pathname === item.path : false;

                    const handleClick = () => {
                      if (item.action === "masjidBillingPortal") {
                        handleMasjidBillingPortal();
                      } else if (item.action === "tvViewer") {
                        navigateToLastTVView(navigate);
                      } else if (item.path) {
                        navigate(item.path);
                      }
                    };

                    return (
                      <DropdownMenuItem
                        key={item.text}
                        onClick={handleClick}
                        className="cursor-pointer rounded-lg px-3 py-2 text-sm"
                        style={{
                          color: active ? BRAND.gold : palette.text,
                          background: active ? palette.chip : "transparent",
                        }}
                      >
                        <Icon className="mr-2 h-4 w-4" style={{ color: palette.muted }} />
                        <span>{item.text}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}

            {/* Settings (after Manage) */}
            {(() => {
              const settingsItem = (menuStructure.main || []).find(
                (i) => i.path === "/settings"
              );
              if (!settingsItem) return null;
              const active = location.pathname === settingsItem.path;
              return (
                <button
                  key={settingsItem.text}
                  onClick={() => navigate(settingsItem.path)}
                  className="relative rounded-full px-4 py-2 text-sm transition-colors"
                  style={{
                    color: active ? BRAND.gold : palette.text,
                    fontWeight: active ? 600 : 450,
                    background: active ? palette.chip : "transparent",
                  }}
                  onMouseEnter={(e) => {
                    if (!active) e.currentTarget.style.color = BRAND.gold;
                  }}
                  onMouseLeave={(e) => {
                    if (!active) e.currentTarget.style.color = palette.text;
                  }}
                >
                  {settingsItem.text}
                </button>
              );
            })()}

            {/* Approvals Dropdown */}
            {menuStructure.approvals && (
              <DropdownMenu>
                <DropdownMenuTrigger asChild>
                  <button
                    type="button"
                    className="inline-flex items-center gap-1.5 rounded-full px-4 py-2 text-sm transition-colors"
                    style={{ color: palette.text, fontWeight: 450 }}
                    onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.gold)}
                    onMouseLeave={(e) => (e.currentTarget.style.color = palette.text)}
                  >
                    <span>{menuStructure.approvals.label}</span>
                    <ChevronDown className="h-3.5 w-3.5" style={{ color: palette.muted }} />
                  </button>
                </DropdownMenuTrigger>
                <DropdownMenuContent
                  align="start"
                  className="w-56 rounded-xl p-1.5"
                  style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
                >
                  {menuStructure.approvals.items.map((item) => {
                    const Icon = item.icon;
                    const active = location.pathname === item.path;

                    return (
                      <DropdownMenuItem
                        key={item.text}
                        onClick={() => navigate(item.path)}
                        className="cursor-pointer rounded-lg px-3 py-2 text-sm"
                        style={{
                          color: active ? BRAND.gold : palette.text,
                          background: active ? palette.chip : "transparent",
                        }}
                      >
                        <Icon className="mr-2 h-4 w-4" style={{ color: palette.muted }} />
                        <span>{item.text}</span>
                      </DropdownMenuItem>
                    );
                  })}
                </DropdownMenuContent>
              </DropdownMenu>
            )}
          </div>

          {/* Right cluster */}
          <div className="flex items-center gap-1.5 sm:gap-2">
            {/* Theme toggle */}
            <button
              onClick={toggleTheme}
              className="hidden h-9 w-9 items-center justify-center rounded-full transition-colors sm:inline-flex"
              style={{ color: palette.muted }}
              onMouseEnter={(e) => (e.currentTarget.style.color = BRAND.gold)}
              onMouseLeave={(e) => (e.currentTarget.style.color = palette.muted)}
              aria-label="Toggle theme"
            >
              {isDark ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </button>

            {/* Hairline divider */}
            <div
              className="hidden h-5 w-px lg:block"
              style={{ background: palette.border }}
            />

            {/* Desktop User Menu */}
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  variant="ghost"
                  className="hidden h-9 gap-1.5 rounded-full px-2 transition-colors hover:bg-transparent lg:flex"
                >
                  <Avatar
                    className="h-7 w-7"
                    style={{ border: `1px solid ${palette.border}` }}
                  >
                    <AvatarFallback
                      className="text-xs font-semibold"
                      style={{
                        background: BRAND.gold,
                        color: "#fff",
                        fontFamily: EDITORIAL_FONT.serif,
                        fontSize: 13,
                      }}
                    >
                      {user?.email?.charAt(0).toUpperCase() || "U"}
                    </AvatarFallback>
                  </Avatar>
                  <span
                    className="hidden max-w-[120px] truncate text-sm md:inline"
                    style={{ color: palette.text, fontWeight: 500 }}
                  >
                    {user?.email?.split("@")[0]}
                  </span>
                  <ChevronDown className="h-3.5 w-3.5" style={{ color: palette.muted }} />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent
                align="end"
                className="w-60 rounded-xl p-1.5"
                style={{ background: palette.bg, border: `1px solid ${palette.border}` }}
              >
                <DropdownMenuLabel className="px-3 py-2.5">
                  <div className="flex flex-col gap-1">
                    <p
                      className="truncate text-sm leading-none"
                      style={{
                        fontFamily: EDITORIAL_FONT.serif,
                        fontSize: 16,
                        fontWeight: 500,
                        color: palette.text,
                      }}
                    >
                      {user?.email}
                    </p>
                    <p
                      className="text-[10px] uppercase leading-none"
                      style={{ letterSpacing: "0.14em", color: palette.muted }}
                    >
                      {userProfile?.isAdmin ? userProfile?.role || "Admin" : "User"}
                    </p>
                  </div>
                </DropdownMenuLabel>
                <DropdownMenuSeparator style={{ background: palette.border }} />
                <DropdownMenuItem
                  onClick={handleLogout}
                  className="cursor-pointer rounded-lg px-3 py-2 text-sm"
                  style={{ color: BRAND.terracotta }}
                >
                  <LogOut className="mr-2 h-4 w-4" />
                  Log out
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>

            {/* Mobile Menu Button */}
            <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
              <SheetTrigger asChild>
                <button
                  className="inline-flex h-9 w-9 items-center justify-center rounded-full lg:hidden"
                  style={{ color: palette.text }}
                  aria-label="Open menu"
                >
                  <Menu className="h-5 w-5" />
                </button>
              </SheetTrigger>
              <SheetContent
                side="right"
                className="w-72 p-0"
                style={{
                  background: palette.bg,
                  borderColor: palette.border,
                  color: palette.text,
                }}
              >
                <div className="flex h-full flex-col">
                  {/* Mobile User Info */}
                  <div
                    className="p-6"
                    style={{ borderBottom: `1px solid ${palette.border}` }}
                  >
                    <div className="flex items-center gap-3">
                      <Avatar
                        className="h-12 w-12"
                        style={{ border: `1px solid ${palette.border}` }}
                      >
                        <AvatarFallback
                          className="text-lg font-semibold"
                          style={{
                            background: BRAND.gold,
                            color: "#fff",
                            fontFamily: EDITORIAL_FONT.serif,
                          }}
                        >
                          {user?.email?.charAt(0).toUpperCase() || "U"}
                        </AvatarFallback>
                      </Avatar>
                      <div className="flex-1 overflow-hidden">
                        <p
                          className="truncate"
                          style={{
                            fontFamily: EDITORIAL_FONT.serif,
                            fontSize: 16,
                            fontWeight: 500,
                            color: palette.text,
                          }}
                        >
                          {user?.email}
                        </p>
                        <p
                          className="text-[10px] uppercase"
                          style={{ letterSpacing: "0.14em", color: palette.muted }}
                        >
                          {userProfile?.isAdmin ? userProfile?.role || "Admin" : "User"}
                        </p>
                      </div>
                    </div>
                  </div>

                  {/* Mobile Navigation */}
                  <MobileMenu
                    menuStructure={menuStructure}
                    currentPath={location.pathname}
                    onNavigate={navigate}
                    onClose={() => setMobileOpen(false)}
                    onMasjidBillingPortal={handleMasjidBillingPortal}
                    onTVViewer={() => navigateToLastTVView(navigate)}
                    billingLoading={billingLoading}
                  />

                  {/* Mobile theme toggle */}
                  <div
                    className="px-4 py-3"
                    style={{ borderTop: `1px solid ${palette.border}` }}
                  >
                    <button
                      onClick={toggleTheme}
                      className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-left text-sm transition-colors"
                      style={{ color: palette.text }}
                    >
                      {isDark ? (
                        <Sun className="h-4 w-4" style={{ color: BRAND.gold }} />
                      ) : (
                        <Moon className="h-4 w-4" style={{ color: palette.muted }} />
                      )}
                      {isDark ? "Light mode" : "Dark mode"}
                    </button>
                  </div>

                  {/* Mobile Logout Button */}
                  <div
                    className="p-4"
                    style={{ borderTop: `1px solid ${palette.border}` }}
                  >
                    <button
                      onClick={handleLogout}
                      className="flex w-full items-center justify-center gap-2 rounded-full px-4 py-2.5 text-sm font-medium"
                      style={{
                        border: `1px solid ${palette.border}`,
                        color: BRAND.terracotta,
                        background: "transparent",
                      }}
                    >
                      <LogOut className="h-4 w-4" />
                      Log out
                    </button>
                  </div>
                </div>
              </SheetContent>
            </Sheet>
          </div>
        </div>
      </nav>

      {/* Page Content */}
      <main className="mx-auto max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
        {children}
      </main>
    </div>
  );
}
